import { authFetch } from "../../api/authFetch";
import {
  buildPygAnaliticoStructure,
  calcMargenComercialMensual,
  calcResultadoFinalMensual,
  calcVentaMensual,
  calcVentaPipelineBreakdownMensual,
  calcVentaTotalMensual,
  calcularProyeccionPorMes,
} from "./pygAnaliticoBuild";

const fetchJsonArray = async (url) => {
  const res = await authFetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(res.statusText || `HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
};

async function loadPygGroupStructure(
  apiUrl,
  { year, empresa, nivel1, omitirGeneralStage, nivel2 },
) {
  const appendFilters = (url) => {
    let next = url;
    if (nivel1) next += `&nivel1=${encodeURIComponent(nivel1)}`;
    if (omitirGeneralStage) next += "&omitirGeneralStage=1";
    if (nivel2) next += `&nivel2=${encodeURIComponent(nivel2)}`;
    return next;
  };

  const empresaEnc = encodeURIComponent(empresa);
  const base = `${apiUrl}/api/pyg`;
  const yearQ = `year=${year}&empresa=${empresaEnc}`;

  const [
    rawData,
    proyeccionVentas,
    proyeccionCompras,
    pedidosVenta,
    pedidosCompra,
    facturasBorradorVenta,
  ] = await Promise.all([
    fetchJsonArray(appendFilters(`${base}/saldos-npgc?${yearQ}`)),
    fetchJsonArray(appendFilters(`${base}/proyeccion_ventas?${yearQ}`)),
    fetchJsonArray(appendFilters(`${base}/proyeccion_compras?${yearQ}`)),
    fetchJsonArray(appendFilters(`${base}/pedidos_venta?${yearQ}`)),
    fetchJsonArray(appendFilters(`${base}/pedidos_compra?${yearQ}`)),
    fetchJsonArray(appendFilters(`${base}/facturas_venta_borrador?${yearQ}`)),
  ]);

  return buildPygAnaliticoStructure(rawData, {
    resultadoProyeccionVentas: calcularProyeccionPorMes(proyeccionVentas, year),
    resultadoProyeccionCompras: calcularProyeccionPorMes(
      proyeccionCompras,
      year,
    ),
    resultadoPedidosVenta: calcularProyeccionPorMes(pedidosVenta, year),
    resultadoPedidosCompra: calcularProyeccionPorMes(pedidosCompra, year),
    resultadoFacturasBorradorVenta: calcularProyeccionPorMes(
      facturasBorradorVenta,
      year,
    ),
  });
}

/** Fila RESULTADO FINAL de la tabla PyG analítico, por mes. */
export async function fetchResultadoFinalMensual(apiUrl, params) {
  const { groupMap } = await loadPygGroupStructure(apiUrl, params);
  return calcResultadoFinalMensual(groupMap);
}

/** Epígrafe 1 (venta / cifra de negocios), por mes. */
export async function fetchVentaMensual(apiUrl, params) {
  const { groupMap } = await loadPygGroupStructure(apiUrl, params);
  return calcVentaMensual(groupMap);
}

/** Venta total (contable + proyección + pedidos + borradores), por mes. */
export async function fetchVentaTotalMensual(apiUrl, params) {
  const { groupMap } = await loadPygGroupStructure(apiUrl, params);
  return calcVentaTotalMensual(groupMap);
}

/** Desglose pipeline ventas, por mes. */
export async function fetchVentaPipelineBreakdownMensual(apiUrl, params) {
  const { groupMap } = await loadPygGroupStructure(apiUrl, params);
  return calcVentaPipelineBreakdownMensual(groupMap);
}

/** Una carga por año → venta total, pipeline y resultado (evita triplicar peticiones). */
export async function fetchPygChartSeriesByYears(apiUrl, params, years) {
  const list = Array.isArray(years) ? years : [];
  const ventaByYear = {};
  const ventaContableByYear = {};
  const pipelineByYear = {};
  const resultadoByYear = {};

  if (!list.length) {
    return {
      ventaByYear,
      ventaContableByYear,
      pipelineByYear,
      resultadoByYear,
    };
  }

  const settled = await Promise.allSettled(
    list.map(async (yearNum) => {
      const { groupMap } = await loadPygGroupStructure(apiUrl, {
        ...params,
        year: yearNum,
      });
      return {
        year: yearNum,
        venta: calcVentaTotalMensual(groupMap),
        ventaContable: calcVentaMensual(groupMap),
        pipeline: calcVentaPipelineBreakdownMensual(groupMap),
        resultado: calcResultadoFinalMensual(groupMap),
      };
    }),
  );

  settled.forEach((entry) => {
    if (entry.status !== "fulfilled") return;
    const { year, venta, ventaContable, pipeline, resultado } = entry.value;
    ventaByYear[year] = venta;
    ventaContableByYear[year] = ventaContable;
    pipelineByYear[year] = pipeline;
    resultadoByYear[year] = resultado;
  });

  return { ventaByYear, ventaContableByYear, pipelineByYear, resultadoByYear };
}

/** Carga varias series anuales en paralelo (gráficos comparativos). */
export async function fetchSerieMensualByYears(apiUrl, params, years, fetchFn) {
  const list = Array.isArray(years) ? years : [];
  if (!list.length) return {};
  const results = await Promise.all(
    list.map((year) => fetchFn(apiUrl, { ...params, year })),
  );
  const next = {};
  list.forEach((year, idx) => {
    next[year] = results[idx];
  });
  return next;
}

/** Margen comercial (ventas + coste + pipeline), por mes. */
export async function fetchMargenComercialMensual(apiUrl, params) {
  const { groupMap, groupOrder } = await loadPygGroupStructure(apiUrl, params);
  return calcMargenComercialMensual(groupMap, groupOrder);
}
